import { Card, CardActions, CardHeader, CardMedia, Chip, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import ResourceDto from '../../dtos/Resources/ResourceDto';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AuthUtil from '../Utils/AuthUtil';

export default function ResourcePreview(props) {
    const { resource, confirmDeleteResource, editResource } = props;
    const { t } = useTranslation();

    const navigate = useNavigate();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    // Mirrors the check in ResourceService.UpdateResourceAsync: an org admin may edit the body of
    // a resource belonging to their own org. Public resources are excluded -- every org reads
    // those, so rewriting one would change what all the others see. The server enforces this
    // regardless; hiding the button just avoids offering an action that would be refused.
    const canEditContent = useMemo(() => {
        if (isSuperAdmin) {
            return true;
        }

        if (!AuthUtil.isAdmin(userToken) || resource.isPublic) {
            return false;
        }

        const usersOrgId = AuthUtil.getUsersOrgId(userToken);

        return (resource.ctOrgs ?? []).some((org) => org.ogId === usersOrgId);
    }, [isSuperAdmin, userToken, resource]);

    return (
        <Grid item md={12} >
            <Card sx={{maxWidth: 300, margin: "0 auto", padding: "0.1em"}}>
                <CardHeader
                    title={resource.rsTitle}
                />
                {resource.resourceImage &&
                    <CardMedia
                        component={'img'}
                        sx={{maxHeight: 200,padding: "1em 1em 0 1em", objectFit: 'contain', marginBottom: '10px'}}
                        image={resource.resourceImage.url}
                    />
                }

                <Typography variant={"body2"} color={'text.secondary'} sx={{ margin: '0 10px'}}>
                    {resource.rsShortDescription}
                </Typography>
                {resource.resourceType && <Chip variant={'outlined'} label={resource.resourceType} />}
                {resource.resourceTopics.map((value) => {
                    return <Chip key={value.id} label={value.name} />
                })}
                <CardActions>
                    <Button size={'small'} onClick={() => navigate('/resource/' + resource.rsId)}>{t('resourcePreview.readMore')}</Button>
                    {canEditContent &&
                        <div className={'resource-admin-buttons'}>
                        <Button size={'small'} color={'success'} onClick={() => { editResource(resource) }}>{t('common.edit')}</Button>
                        {/* Deleting stays super-admin only: an org admin may reword a resource, not remove it. */}
                        {isSuperAdmin &&
                            <Button size={'small'} color={'error'} onClick={() => { confirmDeleteResource(resource)}}>{t('common.delete')}</Button>
                        }
                        </div>
                    }
                </CardActions>
            </Card>
        </Grid>
    )
}

ResourcePreview.propTypes = {
    resource: PropTypes.objectOf(ResourceDto),
    confirmDeleteResource: PropTypes.func.isRequired,
    editResource: PropTypes.func.isRequired,
}
